import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getBackendClient } from "@/lib/backend";
import {
  createStandaloneSpeech,
  dictationBusy,
  IDLE_SPEECH,
  type SpeechState,
  type SpeechTurn,
} from "../speech/controller";
import { UNAVAILABLE_SPEECH, type SpeechCapabilities } from "@/lib/assistant/speech-contract";
import type { DirectTextTurn } from "./use-direct-text";

interface Preferences {
  dictation: boolean;
  readAloud: boolean;
  readRepliesAutomatically: boolean;
}
function loadPreferences(key: string | null): Preferences {
  try {
    const saved = key ? JSON.parse(localStorage.getItem(key) ?? "null") : null;
    return {
      dictation: saved?.dictation === true,
      readAloud: saved?.readAloud === true,
      readRepliesAutomatically:
        saved?.readAloud === true && saved?.readRepliesAutomatically === true,
    };
  } catch {
    return { dictation: false, readAloud: false, readRepliesAutomatically: false };
  }
}

function hasAvailableFlag(section: unknown): boolean {
  if (typeof section !== "object" || section === null) return false;
  return typeof (section as Record<string, unknown>).available === "boolean";
}

function isValidSpeechCapabilities(
  data: SpeechCapabilities | null | undefined,
): data is SpeechCapabilities {
  if (!data) return false;
  return (
    hasAvailableFlag(data.approvalCapture) &&
    hasAvailableFlag(data.dictation) &&
    hasAvailableFlag(data.readAloud)
  );
}

/**
 * Claims every local assistant finalization exactly once and reads the ones
 * eligible now. The claim is consumed even while reading is disabled or the
 * mode is excluded, so a later preference change or remount never adopts an
 * older reply.
 */
function readFinalizedTurns(
  turns: readonly DirectTextTurn[] | undefined,
  shouldRead: boolean,
  read: (turn: SpeechTurn) => void,
): void {
  for (const turn of turns ?? []) {
    const claimed =
      turn.isFinal && turn.role === "assistant" && turn.claimAutomaticRead?.() === true;
    if (claimed && shouldRead) read(turn);
  }
}
export function useStandaloneSpeech(input: {
  actorId: string | null;
  mode: "text" | "live";
  textPending: boolean;
  conversationId: string | null;
  transitioning: () => boolean;
  appendDraft: (text: string) => void;
  turns?: readonly DirectTextTurn[];
}) {
  const key = input.actorId ? `xan:optional-speech:v1:${location.origin}:${input.actorId}` : null;
  const [preferences, setPreferences] = useState(() => loadPreferences(key));
  const [state, setState] = useState<SpeechState>(IDLE_SPEECH);
  const capabilitiesQuery = useQuery({
    queryKey: ["assistant-speech-capabilities", input.actorId],
    enabled: Boolean(input.actorId),
    retry: false,
    gcTime: 0,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const { data, error } = await getBackendClient().functions.invoke<SpeechCapabilities>(
        "assistant-speech",
        { body: { action: "capabilities" }, retry: false },
      );
      return error || !isValidSpeechCapabilities(data) ? UNAVAILABLE_SPEECH : data;
    },
  });
  const capabilities = capabilitiesQuery.data ?? UNAVAILABLE_SPEECH;
  const policy = useRef({ ...input, preferences, capabilities });
  useLayoutEffect(() => {
    policy.current = { ...input, preferences, capabilities };
  }, [input, preferences, capabilities]);
  const mutation = useMutation({
    retry: false,
    gcTime: 0,
    mutationFn: async ({
      body,
      signal,
    }: {
      body: Record<string, unknown>;
      signal: AbortSignal;
    }) => {
      const response = await getBackendClient().functions.invokeRaw("assistant-speech", {
        body,
        signal,
      });
      if (!response.ok) throw new Error("Standalone speech unavailable");
      return response.json();
    },
  });
  const { mutateAsync, reset } = mutation;
  const invoke = useCallback(
    (body: Record<string, unknown>, signal: AbortSignal) => {
      // Abort the browser transport and discard late results. Already-submitted
      // provider work is not guaranteed to stop and is never promised a refund.
      signal.throwIfAborted();
      return mutateAsync({
        body,
        signal: AbortSignal.any([signal, AbortSignal.timeout(120_000)]),
      }).finally(reset);
    },
    [mutateAsync, reset],
  );

  const canDictate = useCallback(() => {
    const current = policy.current;
    return Boolean(
      current.actorId &&
      !current.transitioning() &&
      current.mode === "text" &&
      !current.textPending &&
      current.preferences.dictation &&
      current.capabilities.dictation.available,
    );
  }, []);
  const canCaptureApproval = useCallback(() => {
    const current = policy.current;
    return Boolean(
      current.actorId && !current.transitioning() && current.capabilities.approvalCapture.available,
    );
  }, []);
  const canRead = useCallback((turn: SpeechTurn) => {
    const current = policy.current;
    return Boolean(
      current.actorId &&
      !current.transitioning() &&
      current.mode === "text" &&
      current.preferences.readAloud &&
      current.capabilities.readAloud.available &&
      current.conversationId &&
      turn.conversationId === current.conversationId,
    );
  }, []);
  const appendDraft = useCallback((text: string) => policy.current.appendDraft(text), []);
  const controller = useMemo(
    () => createStandaloneSpeech({ actorId: input.actorId ?? "", invoke }),
    [input.actorId, invoke],
  );
  useLayoutEffect(() => {
    controller.updateCallbacks({
      canDictate,
      canCaptureApproval,
      canRead,
      appendDraft,
      changed: setState,
    });
  }, [controller, canDictate, canCaptureApproval, canRead, appendDraft]);
  useEffect(() => {
    const release = () => controller.dispose();
    window.addEventListener("pagehide", release);
    return () => {
      window.removeEventListener("pagehide", release);
      release();
    };
  }, [controller]);
  useEffect(() => {
    if (!preferences.dictation || !capabilities.dictation.available || input.mode === "live")
      controller.cancelDictation();
  }, [controller, preferences.dictation, capabilities.dictation.available, input.mode]);
  useLayoutEffect(() => {
    controller.stopReadAloud(true);
  }, [
    controller,
    input.conversationId,
    input.mode,
    preferences.readAloud,
    capabilities.readAloud.available,
    capabilities.readAloud.voice,
    capabilities.readAloud.model,
  ]);
  useEffect(() => {
    // Claim every local finalization, including while disabled or excluded.
    // Surface visibility and later preference changes never adopt old replies.
    readFinalizedTurns(
      input.turns,
      preferences.readRepliesAutomatically && input.mode === "text",
      (turn) => void controller.read(turn),
    );
  }, [controller, input.turns, input.mode, preferences.readRepliesAutomatically]);
  const setPreference = (name: keyof Preferences, enabled: boolean) => {
    if (name === "readRepliesAutomatically" && enabled && !policy.current.preferences.readAloud)
      return;
    // Cancel synchronously, before any late provider callback can observe old policy.
    if (!enabled && name === "dictation") controller.cancelDictation();
    if (!enabled && name === "readAloud") controller.stopReadAloud(true);
    const next = { ...policy.current.preferences, [name]: enabled };
    if (name === "readAloud" && !enabled) next.readRepliesAutomatically = false;
    policy.current.preferences = next;
    setPreferences(next);
    try {
      if (key) localStorage.setItem(key, JSON.stringify(next));
    } catch {
      /* Preferences remain volatile. */
    }
  };
  return {
    ...controller,
    state,
    capabilities,
    preferences,
    setPreference,
    isDictating: () => dictationBusy(controller.state()),
    read: (turn: SpeechTurn) => controller.read(turn),
    startApprovalCapture: controller.startApprovalCapture,
    cancelApprovalCapture: controller.cancelApprovalCapture,
  };
}
export type StandaloneSpeech = ReturnType<typeof useStandaloneSpeech>;
